import { isNil } from 'lodash/fp';
import { QueryState } from 'app/lib/constants';
import {
    GET_SNOWFLAKE_QUERY_STATUS_OPERATION,
    GET_SNOWFLAKE_RESULT_OPERATION,
    SUBMIT_SNOWFLAKE_QUERY_OPERATION
} from 'app/store/__generated__/operations';
import {
    GET_QUERY_RESULTSET,
    GET_QUERY_STATUS,
    SUBMIT_QUERY
} from 'app/store/snowflakeQueries/types.js';
import { selectQueryForPropKey } from 'app/store/snowflakeQueries/selectors.js';
import { doCancelQuery } from 'app/store/snowflakeQueries/actions.js';
import { clientWrapper } from './clientWrapper.js';

const wasQueryCancelled = (query) => isNil(query) || query.state === QueryState.CANCELED;

const getQuery = (getState, queryKey) => selectQueryForPropKey(getState(), { queryKey });

const getQueryResults = (dispatch, id) =>
    clientWrapper(dispatch, { ...GET_SNOWFLAKE_RESULT_OPERATION, id });

const getQueryStatus = (dispatch, id) =>
    clientWrapper(dispatch, { ...GET_SNOWFLAKE_QUERY_STATUS_OPERATION, id });

const pollQueryStatus = (dispatch, isOutdated, id) =>
    new Promise((resolve) => {
        getQueryStatus(dispatch, id).then((data) => {
            dispatch({ type: GET_QUERY_STATUS.SUCCEEDED, params: { id }, payload: data });

            if (!isOutdated() && data.isStillRunning) {
                setTimeout(() => resolve(pollQueryStatus(dispatch, isOutdated, id)), 1000);
            } else {
                resolve();
            }
        });
    });

const submitQuery = (dispatch, sql, meta, context) =>
    clientWrapper(dispatch, { ...SUBMIT_SNOWFLAKE_QUERY_OPERATION, sql, meta, context });

const getQuerySql = (dispatch, params) => clientWrapper(dispatch, params);

const latestRunByQueryKey = new Map();

export function snowflakeQueryMiddleware({ dispatch, getState }) {
    return (next) => async (action) => {
        const { type, getSqlParams, queryKey, meta, context, onSucceeded } = action;

        if (!getSqlParams || !queryKey) {
            return next(action);
        }

        const run = {};
        const isOutdated = () =>
            latestRunByQueryKey.get(queryKey) !== run ||
            wasQueryCancelled(getQuery(getState, queryKey));

        latestRunByQueryKey.set(queryKey, run);

        let queryId = getSqlParams.queryId;

        if (!queryId) {
            const query = getQuery(getState, queryKey);

            if (query?.state === QueryState.RUNNING && query.queryId) {
                dispatch(doCancelQuery({ id: query.queryId, queryKey }));
            }

            dispatch({ type: `${type}/started`, payload: { queryKey }, meta });

            const sqlQueryResult = getSqlParams.skipSqlTranslate
                ? { sql: getSqlParams.sql }
                : await getQuerySql(dispatch, getSqlParams).catch((error) => {
                      if (!isOutdated()) {
                          dispatch({ type: `${type}/failed`, payload: error, meta });
                      }
                  });

            if (!sqlQueryResult || isOutdated()) {
                return;
            }

            dispatch({
                type: `${type}/succeeded`,
                payload: { queryKey, data: sqlQueryResult },
                meta
            });

            const data = await submitQuery(dispatch, sqlQueryResult.sql, meta, context);

            if (isOutdated()) {
                dispatch(doCancelQuery({ id: data.queryId }));
                return;
            }

            queryId = data.queryId;

            dispatch({
                type: SUBMIT_QUERY.SUCCEEDED,
                payload: { queryId },
                meta: { queryKey }
            });
        }

        await pollQueryStatus(dispatch, isOutdated, queryId);

        if (isOutdated()) {
            return;
        }

        const results = await getQueryResults(dispatch, queryId).catch((error) => {
            if (isOutdated()) {
                return;
            }

            dispatch({
                type: GET_QUERY_RESULTSET.FAILED,
                params: { id: queryId },
                meta,
                payload: error
            });
        });

        if (!results || isOutdated()) {
            return;
        }

        dispatch({
            type: GET_QUERY_RESULTSET.SUCCEEDED,
            params: { id: queryId },
            payload: results,
            meta
        });

        if (onSucceeded) {
            onSucceeded({ dispatch, data: results, meta, getState });
        }

        dispatch({
            type: `${type}/ended`,
            payload: { queryKey },
            meta
        });
    };
}
